import { useCallback, useEffect, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useLocation, useNavigate } from "react-router-dom";
import axios from "axios";
import { AuthService } from "../services/auth/Auth";
import {
  setAccessToken,
  setIsAuthenticated,
  setSessionExpired,
  setUser
} from "../Redux/Reducers/appState";
import {
  USER_SESSION_TIMEOUT_MS,
  USER_SESSION_ACTIVITY_KEY,
  USER_SESSION_LOGOUT_KEY,
  USER_SESSION_EXPIRED_MESSAGE,
  createUserSessionChannel,
  publishUserActivity,
  clearUserSessionActivity,
  broadcastUserLogout,
  getRemainingSessionMs,
  shouldHandleUserSessionExpiry,
  getUserSessionExpiryMessage,
  persistUserSessionMessage
} from "../utils/userSessionTimeout";

const ACTIVITY_EVENTS = ["click", "touchstart", "keydown", "scroll"];

export default function UserSessionTimeoutManager() {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const isAuthenticated = useSelector((state) => state.appState.isAuthenticated);

  const timerRef = useRef(null);
  const logoutRef = useRef(false);
  const lastPublishedActivityRef = useRef(0);
  const channelRef = useRef(null);
  const isAuthenticatedRef = useRef(isAuthenticated);

  const clearTimer = useCallback(() => {
    if (timerRef.current) {
      window.clearTimeout(timerRef.current);
      timerRef.current = null;
    }
  }, []);

  const clearClientSession = useCallback(() => {
    dispatch(setUser(null));
    dispatch(setAccessToken(null));
    dispatch(setIsAuthenticated(false));
    dispatch(setSessionExpired(true));
  }, [dispatch]);

  const performLogout = useCallback(
    async ({
      broadcast = false,
      message = USER_SESSION_EXPIRED_MESSAGE,
      reason = "inactive"
    } = {}) => {
      if (logoutRef.current) {
        return;
      }

      logoutRef.current = true;
      clearTimer();

      if (broadcast) {
        broadcastUserLogout({ reason, message });
        channelRef.current?.postMessage({
          type: "logout",
          reason,
          message,
          timestamp: Date.now()
        });
      } else if (message) {
        persistUserSessionMessage(message);
      }

      clearUserSessionActivity();
      clearClientSession();

      try {
        await AuthService.logout({
          id: null,
          email: null,
          ipaddress: null
        });
      } catch (error) {
        console.log(error);
      }

      navigate("/login", { replace: true });
    },
    [clearClientSession, clearTimer, navigate]
  );

  const scheduleTimeout = useCallback(
    (lastActivityAt) => {
      clearTimer();

      const remainingMs = getRemainingSessionMs(lastActivityAt, USER_SESSION_TIMEOUT_MS);

      if (remainingMs <= 0) {
        performLogout({
          broadcast: true,
          message: USER_SESSION_EXPIRED_MESSAGE,
          reason: "inactive"
        });
        return;
      }

      timerRef.current = window.setTimeout(() => {
        performLogout({
          broadcast: true,
          message: USER_SESSION_EXPIRED_MESSAGE,
          reason: "inactive"
        });
      }, remainingMs);
    },
    [clearTimer, performLogout]
  );

  const markActivity = useCallback(
    (force = false) => {
      if (!isAuthenticated || logoutRef.current) {
        return;
      }

      const now = Date.now();
      if (!force && now - lastPublishedActivityRef.current < 1000) {
        return;
      }

      lastPublishedActivityRef.current = now;
      publishUserActivity(now);
      channelRef.current?.postMessage({
        type: "activity",
        timestamp: now
      });
      scheduleTimeout(now);
    },
    [isAuthenticated, scheduleTimeout]
  );

  useEffect(() => {
    isAuthenticatedRef.current = isAuthenticated;
  }, [isAuthenticated]);

  useEffect(() => {
    if (!isAuthenticated) {
      logoutRef.current = false;
      clearTimer();
      return undefined;
    }

    logoutRef.current = false;
    const storedActivity = Number(window.localStorage.getItem(USER_SESSION_ACTIVITY_KEY) || 0);
    const initialActivity = storedActivity > 0 ? storedActivity : Date.now();
    if (!storedActivity) {
      publishUserActivity(initialActivity);
    }
    lastPublishedActivityRef.current = initialActivity;
    scheduleTimeout(initialActivity);

    const handleStorage = (event) => {
      if (event.key === USER_SESSION_ACTIVITY_KEY && event.newValue) {
        const nextActivity = Number(event.newValue || 0);
        if (Number.isFinite(nextActivity) && nextActivity > 0) {
          lastPublishedActivityRef.current = nextActivity;
          scheduleTimeout(nextActivity);
        }
      }

      if (event.key === USER_SESSION_LOGOUT_KEY && event.newValue) {
        const payload = JSON.parse(event.newValue || "{}");
        performLogout({
          broadcast: false,
          message: payload?.message || USER_SESSION_EXPIRED_MESSAGE,
          reason: payload?.reason || "sync"
        });
      }
    };

    const handleBroadcast = (event) => {
      if (event?.data?.type === "activity" && event.data.timestamp) {
        lastPublishedActivityRef.current = event.data.timestamp;
        scheduleTimeout(event.data.timestamp);
      }

      if (event?.data?.type === "logout") {
        performLogout({
          broadcast: false,
          message: event.data.message || USER_SESSION_EXPIRED_MESSAGE,
          reason: event.data.reason || "sync"
        });
      }
    };

    channelRef.current = createUserSessionChannel();
    channelRef.current?.addEventListener("message", handleBroadcast);

    ACTIVITY_EVENTS.forEach((eventName) => {
      window.addEventListener(eventName, markActivity, { passive: true });
    });
    window.addEventListener("storage", handleStorage);

    return () => {
      clearTimer();
      ACTIVITY_EVENTS.forEach((eventName) => {
        window.removeEventListener(eventName, markActivity, { passive: true });
      });
      window.removeEventListener("storage", handleStorage);
      channelRef.current?.removeEventListener("message", handleBroadcast);
      channelRef.current?.close();
      channelRef.current = null;
    };
  }, [clearTimer, isAuthenticated, markActivity, performLogout, scheduleTimeout]);

  useEffect(() => {
    if (!isAuthenticated) {
      return;
    }

    markActivity(true);
  }, [isAuthenticated, location.pathname, markActivity]);

  useEffect(() => {
    const originalFetch = window.fetch.bind(window);
    window.fetch = async (...args) => {
      const response = await originalFetch(...args);

      try {
        const contentType = response.headers.get("content-type") || "";
        if (
          isAuthenticatedRef.current &&
          [401, 403].includes(response.status) &&
          contentType.includes("application/json")
        ) {
          const payload = await response.clone().json();
          if (shouldHandleUserSessionExpiry(payload, response.status)) {
            performLogout({
              broadcast: true,
              message: getUserSessionExpiryMessage(payload),
              reason: "server"
            });
          }
        }
      } catch (error) {
        console.log(error);
      }

      return response;
    };

    const axiosInterceptorId = axios.interceptors.response.use(
      (response) => {
        if (
          isAuthenticatedRef.current &&
          shouldHandleUserSessionExpiry(response?.data, response?.status)
        ) {
          performLogout({
            broadcast: true,
            message: getUserSessionExpiryMessage(response.data),
            reason: "server"
          });
        }

        return response;
      },
      async (error) => {
        const statusCode = error?.response?.status;
        const responsePayload = error?.response?.data;

        if (
          isAuthenticatedRef.current &&
          shouldHandleUserSessionExpiry(responsePayload, statusCode)
        ) {
          await performLogout({
            broadcast: true,
            message: getUserSessionExpiryMessage(responsePayload),
            reason: "server"
          });
        }

        return Promise.reject(error);
      }
    );

    return () => {
      window.fetch = originalFetch;
      axios.interceptors.response.eject(axiosInterceptorId);
    };
  }, [performLogout]);

  return null;
}
